import { IEntityDataBuilder } from './entity-data-builder.interface';
import {
  ALL_LIST_FILTER,
  IEntity,
  IFetchFilterKey,
  ISearchParams,
  REFRESH_FILTER_DELAY,
  SearchParamsEnum,
  SortByEnum,
  SortOrderEnum,
} from './entity-data.data';
import { Settings } from '../settings-data/settings.model';
import { List } from 'immutable';
import { Toggles } from '../features-data/sub-models/toggles.model';
import { EntityTypesEnum } from '../../app.enum';
import { identity, mapValues, pickBy } from 'lodash';
import { UrlService } from '../shared/services/url/url.service';

/**
 * This class is the source of true for all static and shared entity attributes like entity type, quotaName, etc.
 * It should only be used as a superclass; this class is not injectable;
 */
export abstract class EntityDataBuilder<T extends IEntity>
  implements IEntityDataBuilder {
  model: any; // typeof T does not compile
  quotaName: string;
  entityType: EntityTypesEnum;
  listFilterKeys: IFetchFilterKey;
  yearFilterKey: string;
  refreshFiltersDelay: number = REFRESH_FILTER_DELAY;

  constructor(protected urlService: UrlService) {}

  abstract buildEntity(
    props: any,
    settings?: Settings,
    isDirty?: boolean,
    isNew?: boolean,
    recalculate?: boolean
  ): T;

  abstract buildEntities(entitiesProps: any[]): List<T>;

  abstract buildSortItemsComparatorFn(
    sortBy: SortByEnum,
    sortOrder: SortOrderEnum,
    settings?: Settings,
    ...args
  ): (entityA: T, entityB: T) => number;

  abstract buildListItemsFilterFn(
    searchParams?: ISearchParams,
    listFilter?: string,
    settings?: Settings,
    toggles?: Toggles
  ): (entity: T) => boolean;

  abstract buildListFilter(
    entity: T,
    settings?: Settings,
    toggles?: Toggles
  ): string;

  abstract validateSearchParams(
    searchParams: ISearchParams,
    filter: string
  ): boolean;

  buildSearchParams(
    currentSearchParams: ISearchParams,
    newSearchParams: ISearchParams,
    listFilter?: string
  ): ISearchParams {
    const sortBy =
      newSearchParams[SearchParamsEnum.sort_by] ||
      currentSearchParams[SearchParamsEnum.sort_by];
    const sortOrder =
      newSearchParams[SearchParamsEnum.sort_order] ||
      currentSearchParams[SearchParamsEnum.sort_order];
    const listParams =
      listFilter && listFilter !== ALL_LIST_FILTER
        ? { [SearchParamsEnum.list_category]: listFilter }
        : {};
    const yearParams = this.yearFilterKey
      ? {
          [this.yearFilterKey]:
            newSearchParams[this.yearFilterKey] ||
            currentSearchParams[this.yearFilterKey],
        }
      : {};

    const result = {
      [SearchParamsEnum.sort_by]: sortBy,
      [SearchParamsEnum.sort_order]: sortOrder,
      ...listParams,
      ...yearParams,
    };

    return pickBy(result, identity);
  }

  buildSearchParamsFromUrl(url: string): ISearchParams {
    const parsedUrl = this.urlService.parse(url);

    return mapValues(parsedUrl.query, (value, key) => {
      switch (key) {
        case SearchParamsEnum.highlights:
          return Boolean(value);
        case SearchParamsEnum.month:
        case SearchParamsEnum.page:
        case SearchParamsEnum.page_size:
        case SearchParamsEnum.year:
          return parseInt(value, 10);
        default:
          return value;
      }
    }) as ISearchParams;
  }
}
